import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useBulkSelect } from '../hooks/useBulkSelect';

const API = process.env.REACT_APP_API_URL;

const ENTITY_LABELS = {
  program: 'Program',
  component: 'Component',
  goal: 'Goal',
  invoice: 'Invoice',
  targeting_list: 'Targeting List',
  lookup_value: 'Lookup Value',
  email_template: 'Email Template',
  client: 'Client',
  content_project: 'Content Project',
  content_asset: 'Content Asset',
};

const btn = (bg) => ({ padding: '5px 12px', background: bg, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
const input = { padding: '7px 9px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' };
const th = { textAlign: 'left', padding: '8px 12px', color: '#666', fontWeight: 600, fontSize: '12px', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' };
const td = { padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: '13px' };

const fmt = (iso) => {
  if (!iso) return '—';
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  try {
    return new Date(s).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

export default function RecycleBin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const { selected, toggle, toggleAll, clear } = useBulkSelect();
  const itemKey = (item) => `${item.entity_type}-${item.id}`;

  const load = useCallback(() => {
    setLoading(true);
    return axios.get(`${API}/recycle-bin`)
      .then(res => { setItems(Array.isArray(res.data) ? res.data : []); setErr(null); })
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load the recycle bin'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (item, restoreAncestors = false) => {
    setBusy(true);
    try {
      await axios.post(`${API}/recycle-bin/${item.entity_type}/${item.id}/restore`, null,
        restoreAncestors ? { params: { restore_ancestors: true } } : undefined);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.reason === 'ancestor_deleted') {
        const names = (d.ancestors || []).map(a => `${ENTITY_LABELS[a.entity_type] || a.entity_type} "${a.label}"`).join(', ');
        if (window.confirm(`${d.message}\n\nParent(s) still deleted: ${names}\n\nRestore them too?`)) {
          await restore(item, true);
        }
      } else {
        alert('Restore failed: ' + (d?.message || d || e.message));
      }
    } finally {
      setBusy(false);
    }
  };

  const purgeOne = (item) => axios.post(`${API}/recycle-bin/${item.entity_type}/${item.id}/purge`, { confirm: true });

  const purge = async (item) => {
    const typed = window.prompt(
      `Permanently delete "${item.label}" and everything under it?\nThis CANNOT be undone.\n\nType PURGE to confirm:`);
    if (typed !== 'PURGE') return;
    setBusy(true);
    try {
      await purgeOne(item);
      await load();
    } catch (e) {
      alert('Purge failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const types = [...new Set(items.map(i => i.entity_type))];
  const visible = items.filter(i =>
    (!typeFilter || i.entity_type === typeFilter) &&
    (!search || (i.label || '').toLowerCase().includes(search.toLowerCase())));
  const selectedItems = visible.filter(i => selected.has(itemKey(i)));

  const bulkRestore = async () => {
    setBusy(true);
    try {
      for (const item of selectedItems) await restore(item);
      clear();
    } finally {
      setBusy(false);
    }
  };

  const bulkPurge = async () => {
    const typed = window.prompt(
      `Permanently delete ${selectedItems.length} item(s) and everything under them?\nThis CANNOT be undone.\n\nType PURGE to confirm:`);
    if (typed !== 'PURGE') return;
    setBusy(true);
    try {
      for (const item of selectedItems) {
        try { await purgeOne(item); } catch (e) { alert(`Purge failed for "${item.label}": ` + (e.response?.data?.detail || e.message)); }
      }
      clear();
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: '1000px' }}>
      <h2 style={{ color: '#2c3e50', marginBottom: '4px' }}>Recycle Bin</h2>
      <p style={{ color: '#7f8c8d', fontSize: '13px', marginTop: 0 }}>
        Soft-deleted items. <strong>Restore</strong> brings an item (and its contents) back;
        <strong> Purge</strong> permanently removes it. Admin only.
      </p>

      {err && <div style={{ background: '#fdecea', color: '#c0392b', padding: '8px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>{err}</div>}

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
        <select style={input} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {types.map(t => <option key={t} value={t}>{ENTITY_LABELS[t] || t}</option>)}
        </select>
        <input style={{ ...input, minWidth: '220px' }} placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
        <button style={btn('#7f8c8d')} disabled={busy || loading} onClick={load}>↻ Refresh</button>
        <span style={{ marginLeft: 'auto', color: '#95a5a6', fontSize: '12px' }}>{visible.length} item{visible.length === 1 ? '' : 's'}</span>
      </div>

      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '8px 12px', background: '#eef6fc', borderRadius: '6px' }}>
          <span style={{ fontSize: '13px', color: '#2c3e50' }}>{selected.size} selected</span>
          <button style={btn('#27ae60')} disabled={busy} onClick={bulkRestore}>Restore Selected</button>
          <button style={btn('#c0392b')} disabled={busy} onClick={bulkPurge}>Purge Selected</button>
          <button style={btn('#95a5a6')} disabled={busy} onClick={clear}>Clear selection</button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: '24px', color: '#999' }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: '#95a5a6', background: '#f8fafc', borderRadius: '8px' }}>
          The recycle bin is empty.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: '32px' }}>
                <input type="checkbox"
                  checked={visible.length > 0 && visible.every(i => selected.has(itemKey(i)))}
                  onChange={() => toggleAll(visible.map(itemKey))} />
              </th>
              <th style={th}>Item</th>
              <th style={th}>Type</th>
              <th style={th}>Deleted by</th>
              <th style={th}>Deleted</th>
              <th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(item => (
              <tr key={itemKey(item)}>
                <td style={td}>
                  <input type="checkbox" checked={selected.has(itemKey(item))} onChange={() => toggle(itemKey(item))} />
                </td>
                <td style={{ ...td, fontWeight: 500, color: '#2c3e50' }}>{item.label}</td>
                <td style={td}>
                  <span style={{ background: '#eef2f6', borderRadius: '10px', padding: '2px 8px', fontSize: '12px', color: '#555' }}>
                    {ENTITY_LABELS[item.entity_type] || item.entity_type}
                  </span>
                </td>
                <td style={td}>{item.deleted_by_name || '—'}</td>
                <td style={{ ...td, whiteSpace: 'nowrap', color: '#666' }}>{fmt(item.deleted_at)}</td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button style={{ ...btn('#27ae60'), marginRight: '6px' }} disabled={busy} onClick={() => restore(item)}>Restore</button>
                  <button style={btn('#c0392b')} disabled={busy} onClick={() => purge(item)}>Purge</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
